/**
 * CoachAvatar — renders the persona's avatar.
 * Primary: DiceBear CDN (zero local payload, public free SVG generator).
 * Fallback: local SVG in /public/avatars/coaches/ — switched in if the
 * CDN fails to load (offline / blocked / air-gapped install).
 */
import React, { useState } from 'react';
import { Users } from 'lucide-react';

const DICEBEAR_BASE = 'https://api.dicebear.com/7.x/avataaars/svg';

export default function CoachAvatar({ persona, size = 40, className = '', ring = false }) {
  const [stage, setStage] = useState('cdn'); // 'cdn' | 'local' | 'icon'

  if (!persona) {
    return (
      <div
        className={`inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-400 ${className}`}
        style={{ width: size, height: size }}
        aria-label="Coach avatar (none selected)"
      >
        <Users size={Math.floor(size * 0.55)} />
      </div>
    );
  }

  const ringClass = ring ? 'ring-2 ring-indigo-300 ring-offset-2 ring-offset-white' : '';
  const baseClass = `inline-block rounded-full bg-white border border-slate-200 ${ringClass} ${className}`;

  if (stage === 'cdn') {
    const url = `${DICEBEAR_BASE}?seed=${encodeURIComponent(persona.avatar_seed)}&radius=50&backgroundColor=eef2ff,fef3c7,fce7f3,ecfeff,f0fdf4`;
    return (
      <img
        src={url}
        alt={persona.name}
        className={baseClass}
        style={{ width: size, height: size }}
        onError={() => setStage('local')}
        data-testid={`coach-avatar-${persona.id}`}
        data-source="dicebear"
      />
    );
  }
  if (stage === 'local' && persona.fallback_svg) {
    return (
      <img
        src={`/avatars/coaches/${persona.fallback_svg}`}
        alt={persona.name}
        className={baseClass}
        style={{ width: size, height: size }}
        onError={() => setStage('icon')}
        data-testid={`coach-avatar-${persona.id}`}
        data-source="local"
      />
    );
  }
  // Last-resort initials-on-color so the UI never shows a broken image.
  const initials = (persona.name || '')
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const bg = persona.gender === 'female' ? '#fce7f3' : persona.gender === 'male' ? '#dbeafe' : '#e0e7ff';
  const fg = persona.gender === 'female' ? '#9f1239' : persona.gender === 'male' ? '#1e3a8a' : '#3730a3';
  return (
    <div
      className={`${baseClass} inline-flex items-center justify-center font-semibold`}
      style={{ width: size, height: size, backgroundColor: bg, color: fg, fontSize: Math.floor(size * 0.4) }}
      data-testid={`coach-avatar-${persona.id}`}
      data-source="icon"
    >
      {initials || <Users size={Math.floor(size * 0.5)} />}
    </div>
  );
}
